import React from 'react';
import type { DecoratedMergePair } from './types';

interface Props {
  pair: DecoratedMergePair;
  onMerge: () => void;
  onDismiss: () => void;
  onSwap: () => void;
}

function formatAmount(n: number): string {
  const abs = Math.abs(n);
  const formatted = abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return n < 0 ? `-$${formatted}` : `$${formatted}`;
}

export default function MergePair({ pair, onMerge, onDismiss, onSwap }: Props): React.ReactElement {
  const keeperIsA = pair.survivor === 'A';
  const keeper = keeperIsA ? pair.accountA : pair.accountB;
  const loser = keeperIsA ? pair.accountB : pair.accountA;
  const newTotalCount = keeper.transactionCount + loser.transactionCount;
  const newTotalBalance = formatAmount(keeper.balance + loser.balance);

  return (
    <div className="grid gap-5 items-center py-5 px-1 border-t border-lineSoft hover:bg-[#EEF4FB] transition-colors grid-cols-[1fr_32px_1fr_auto]">
      <div className="flex flex-col gap-0.5">
        <span className="text-[10.5px] font-extrabold uppercase tracking-wider text-helper mb-1">
          Will be archived
        </span>
        <div className="text-body font-bold text-helper line-through decoration-ink4">
          {loser.name}
        </div>
        <div className="text-small text-helper mt-0.5">{loser.transactionCount} transactions</div>
        <div className="text-small font-semibold text-ink4 mt-1 tabular-nums">
          {formatAmount(loser.balance)}
        </div>
      </div>
      <div className="text-ink3 text-[22px] text-center font-light">
        {keeperIsA ? '→' : '←'}
      </div>
      <div className="flex flex-col gap-0.5">
        <span className="text-[10.5px] font-extrabold uppercase tracking-wider text-ink mb-1">
          Keep
        </span>
        <div className="text-body font-bold text-ink">{keeper.name}</div>
        <div className="text-small text-helper mt-0.5">{keeper.transactionCount} transactions</div>
        <div className="text-small font-semibold text-ink mt-1 tabular-nums">
          {formatAmount(keeper.balance)}
        </div>
        <div className="text-tiny text-teal-dark font-semibold mt-1.5">
          New total: {newTotalCount} transactions · {newTotalBalance}
        </div>
      </div>
      <div className="flex gap-1.5 items-center flex-shrink-0">
        <button
          type="button"
          onClick={onSwap}
          className="bg-transparent border-none text-teal-dark text-small font-semibold cursor-pointer px-2 inline-flex items-center gap-1"
        >
          ↔ Swap
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="h-8 px-3 border border-line bg-white text-ink2 text-small font-semibold rounded-md cursor-pointer"
        >
          Dismiss
        </button>
        <button
          type="button"
          onClick={onMerge}
          className="h-8 px-3.5 bg-ink text-white text-small font-bold rounded-md cursor-pointer border-none"
        >
          Merge
        </button>
      </div>
    </div>
  );
}